import moment from 'moment';
import { Component } from 'react';
import { connect } from 'react-redux';
import { compose } from 'recompose';
import { ChartFragment, ChartFragmentRS } from 'models/api-response';
import { FormFieldInputType } from 'models/enum';
import { ChartActionsComponentProps, ChartComponentProps, ChartMetaFormField, MarDosageRecord } from 'models/ui';
import { DateFormatByLocale, ISO_8601_FORMAT, NAV_ID } from 'constants/app.constant';
import { chartHelper } from 'helpers';
import { formatDate } from 'helpers/datetime.helper';
import { appSelectors } from 'redux/ducks/app';
import { withChartLogic } from 'components/common';
import { DOSE_MODE, FormField, FormFieldLabel } from './constants';
import { MarSidebarProps } from './MarDosageSidebar';
import MarHoldAndDiscontinueFormSidebarView, { MarHoldAndDiscontinueFormSidebarViewProps } from './MarHoldAndDiscontinueFormSidebarView';
import MarHoldAndDiscontinueFormSidebarViewV2 from './MarHoldAndDiscontinueFormSidebarViewV2';
import { applyDosageHold } from './helper';

/** The Hold and Discontinue forms are field-for-field identical, so one builder drives both. */
interface ModeFieldConfig {
  date: FormField;
  dateLabel: FormFieldLabel;
  time: FormField;
  timeLabel: FormFieldLabel;
  timeOffset: FormField;
  timeOffsetLabel: FormFieldLabel;
  comments: FormField;
  commentsLabel: FormFieldLabel;
  firstInitial: FormField;
  lastInitial: FormField;
}

const MODE_FIELD_CONFIGS: { [mode: string]: ModeFieldConfig } = {
  [DOSE_MODE.HOLD]: {
    date: FormField.HOLD_DATE,
    dateLabel: FormFieldLabel.HOLD_DATE,
    time: FormField.HOLD_TIME,
    timeLabel: FormFieldLabel.HOLD_TIME,
    timeOffset: FormField.HOLD_TIME_OFFSET,
    timeOffsetLabel: FormFieldLabel.HOLD_TIME_OFFSET,
    comments: FormField.HOLD_COMMENTS,
    commentsLabel: FormFieldLabel.HOLD_COMMENTS,
    firstInitial: FormField.HOLD_FIRST_INITIAL,
    lastInitial: FormField.HOLD_LAST_INITIAL
  },
  [DOSE_MODE.DISCONTINUE]: {
    date: FormField.DISCONTINUE_DATE,
    dateLabel: FormFieldLabel.DISCONTINUE_DATE,
    time: FormField.DISCONTINUE_TIME,
    timeLabel: FormFieldLabel.DISCONTINUE_TIME,
    timeOffset: FormField.DISCONTINUE_TIME_OFFSET,
    timeOffsetLabel: FormFieldLabel.DISCONTINUE_TIME_OFFSET,
    comments: FormField.DISCONTINUE_COMMENTS,
    commentsLabel: FormFieldLabel.DISCONTINUE_COMMENTS,
    firstInitial: FormField.DISCONTINUE_FIRST_INITIAL,
    lastInitial: FormField.DISCONTINUE_LAST_INITIAL
  }
};

export interface MarHoldAndDiscontinueFormSidebarProps extends ChartComponentProps {
  selectedDosageRecord: MarDosageRecord;
  doseFormMode: string;
  fragments: ChartFragment[];
  isShowEHRMar?: boolean;
  chartingTime?: string;
  onUpdate: Function;
  saveChartData: (payload: object) => Promise<ChartFragmentRS>;
  onClose: Function;
  presaveFragment: Function;
}

class MarHoldAndDiscontinueFormSidebar extends Component<MarHoldAndDiscontinueFormSidebarProps> {
  componentDidUpdate(prevProps: Readonly<MarSidebarProps>) {
    if (this.props.selectedDosageRecord && prevProps.selectedDosageRecord !== this.props.selectedDosageRecord) {
      this.props.initState(this.buildFormFields());
    }
  }

  getFormattedModeDate = (fieldName: string): string => {
    const { selectedDosageRecord, locale, isShowEHRMar, chartingTime } = this.props;
    const dateValue = isShowEHRMar && chartingTime ? chartingTime : selectedDosageRecord[fieldName];
    const inputFormat = isShowEHRMar && chartingTime ? ISO_8601_FORMAT : Object.values(DateFormatByLocale);

    return formatDate({
      date: moment(dateValue, inputFormat).toDate(),
      locale
    });
  };

  buildModeFormFields = (mode: ModeFieldConfig): Map<string, ChartMetaFormField> => {
    const { createFormField } = chartHelper;
    const { selectedDosageRecord, isShowEHRMar, isAuthor } = this.props;
    const formFieldMapData = new Map();

    formFieldMapData.set(mode.date, createFormField({ name: mode.date, type: FormFieldInputType.DATE, label: mode.dateLabel, value: this.getFormattedModeDate(mode.date) }));
    // Authors chart an offset from the phase start rather than a wall-clock time.
    if (isAuthor) {
      formFieldMapData.set(
        mode.timeOffset,
        createFormField({ name: mode.timeOffset, type: FormFieldInputType.TEXT_BOX, label: mode.timeOffsetLabel, value: selectedDosageRecord[mode.timeOffset] || '00:00' })
      );
    } else {
      formFieldMapData.set(mode.time, createFormField({ name: mode.time, type: FormFieldInputType.TIME, label: mode.timeLabel, value: selectedDosageRecord[mode.time] }));
    }
    formFieldMapData.set(
      mode.comments,
      createFormField({ name: mode.comments, type: FormFieldInputType.TEXT_AREA, label: mode.commentsLabel, value: selectedDosageRecord[mode.comments] })
    );
    formFieldMapData.set(
      mode.firstInitial,
      createFormField({
        name: mode.firstInitial,
        type: FormFieldInputType.TEXT_BOX,
        label: isShowEHRMar ? FormFieldLabel.INITIALS : FormFieldLabel.FIRST_INITIAL,
        value: selectedDosageRecord[mode.firstInitial]
      })
    );
    formFieldMapData.set(
      mode.lastInitial,
      createFormField({ name: mode.lastInitial, type: FormFieldInputType.TEXT_BOX, label: FormFieldLabel.LAST_INITIAL, value: selectedDosageRecord[mode.lastInitial] })
    );

    return formFieldMapData;
  };

  buildFormFields = (): Map<string, ChartMetaFormField> => {
    const mode = MODE_FIELD_CONFIGS[this.props.doseFormMode];
    return mode ? this.buildModeFormFields(mode) : new Map();
  };

  buildHoldChartData = (fragment: ChartFragment) => {
    const { formFieldMap, isAuthor, locale, selectedDosageRecord } = this.props;
    const holdFields = {
      [FormField.HOLD_DATE]: formatDate({ date: moment(formFieldMap.get(FormField.HOLD_DATE).value, Object.values(DateFormatByLocale)).toDate(), locale }),
      [FormField.HOLD_COMMENTS]: formFieldMap.get(FormField.HOLD_COMMENTS).value,
      [FormField.HOLD_FIRST_INITIAL]: formFieldMap.get(FormField.HOLD_FIRST_INITIAL).value.toUpperCase(),
      [FormField.HOLD_LAST_INITIAL]: formFieldMap.get(FormField.HOLD_LAST_INITIAL).value.toUpperCase()
    };
    if (isAuthor) {
      holdFields[FormField.HOLD_TIME_OFFSET] = formFieldMap.get(FormField.HOLD_TIME_OFFSET).value;
    } else {
      holdFields[FormField.HOLD_TIME] = formFieldMap.get(FormField.HOLD_TIME).value;
    }

    const targetDosages = selectedDosageRecord.holdDosageTimes;
    return targetDosages?.length
      ? { ...fragment.chartData, dosageTimes: applyDosageHold(selectedDosageRecord, targetDosages, holdFields) }
      : { ...fragment.chartData, ...holdFields, [FormField.IS_HOLD]: true };
  };

  buildDiscontinueChartData = (fragment: ChartFragment) => {
    const { formFieldMap, isAuthor, locale } = this.props;
    const discontinueFields = {
      [FormField.DISCONTINUE_DATE]: formatDate({ date: moment(formFieldMap.get(FormField.DISCONTINUE_DATE).value, Object.values(DateFormatByLocale)).toDate(), locale }),
      [FormField.DISCONTINUE_COMMENTS]: formFieldMap.get(FormField.DISCONTINUE_COMMENTS).value,
      [FormField.DISCONTINUE_FIRST_INITIAL]: formFieldMap.get(FormField.DISCONTINUE_FIRST_INITIAL).value.toUpperCase(),
      [FormField.DISCONTINUE_LAST_INITIAL]: formFieldMap.get(FormField.DISCONTINUE_LAST_INITIAL).value.toUpperCase(),
      [FormField.IS_DISCONTINUED]: true
    };
    if (isAuthor) {
      discontinueFields[FormField.DISCONTINUE_TIME_OFFSET] = formFieldMap.get(FormField.DISCONTINUE_TIME_OFFSET).value;
    } else {
      discontinueFields[FormField.DISCONTINUE_TIME] = formFieldMap.get(FormField.DISCONTINUE_TIME).value;
    }

    return { ...fragment.chartData, ...discontinueFields };
  };

  onClickSave = () => {
    const { fragments, selectedDosageRecord, doseFormMode, handleSaveClick, onUpdate, presaveFragment } = this.props;
    const fragment = fragments.find((item) => item.fragmentId === selectedDosageRecord.fragmentId);
    const updatedChartData = doseFormMode === DOSE_MODE.HOLD ? this.buildHoldChartData(fragment) : this.buildDiscontinueChartData(fragment);
    const updatedFragment: ChartFragment = {
      ...presaveFragment(fragment),
      chartData: updatedChartData,
      linkedFragmentId: fragment.linkedFragmentId || fragment.fragmentId
    };

    handleSaveClick([updatedFragment], { navId: NAV_ID.PHARMACY, afterSave: onUpdate });
  };

  render() {
    const { formFieldMap, selectedDosageRecord, doseFormMode, formSubmittedCount, hasUnsavedChanges, handleDiscardClick, onClose, isAuthor, sidebarProps, isShowEHRMar } =
      this.props;
    const chartActionsProps: ChartActionsComponentProps = {
      enableSaveButton: hasUnsavedChanges,
      onSaveClick: this.onClickSave,
      onCancelClick: isShowEHRMar ? undefined : () => handleDiscardClick(undefined, this.buildFormFields(), { selectorToScroll: '.sidebar__container' }),
      saveButtonText: doseFormMode === DOSE_MODE.HOLD ? 'Hold Medication' : 'Discontinue Medication'
    };
    const viewProps: MarHoldAndDiscontinueFormSidebarViewProps = {
      formFieldMap,
      selectedDosageRecord,
      doseFormMode,
      formSubmittedCount,
      chartActionsProps,
      onCloseClick: onClose,
      isAuthor,
      isShowEHRMar,
      sidebarProps
    };
    return isShowEHRMar ? <MarHoldAndDiscontinueFormSidebarViewV2 {...viewProps} /> : <MarHoldAndDiscontinueFormSidebarView {...viewProps} />;
  }
}

const mapStateToProps = (state) => ({
  selectedNavId: appSelectors.getNavId(state)
});

export { MarHoldAndDiscontinueFormSidebar as BaseMarHoldAndDiscontinueFormSidebar };
export default compose(connect(mapStateToProps), withChartLogic)(MarHoldAndDiscontinueFormSidebar);
